import {
  DepartmentsEnum,
  IntegerFilterInput,
  SortOrder,
  SponsoredProductInput,
} from '@moonpig/web-explore-types-graphql'

import { RouterType } from '@moonpig/web-core-routing'
import { removePreAppliedFilters } from './removePreAppliedFilters'
import { SearchStoreState } from '../types'
import {
  ApplicableFilter,
  FilterItem,
  LoadFiltersResponse,
  RecipientWithReminders,
  SearchFilter,
  SearchRequestResponse,
  SearchService,
  SelectedSuggestion,
} from '../../services/types/services'
import { FilteringEvent, PageType } from '../../types'
import { extractIntegerFilters } from './extractIntegerFilters'

type BuildInitialStateArgs = {
  pageTitle: string
  pageType: PageType
  router: RouterType
  department: DepartmentsEnum[]
  initialResponse: SearchRequestResponse
  redirectedFrom?: DepartmentsEnum
  redirectionSource?: 'smart_redirection' | 'no_results'
  searchTerm: string
  correctedSearchTerm?: string
  selectedSuggestion?: SelectedSuggestion
  isTrendingSuggestion?: boolean
  promotionId?: string
  collection?: string
  initialFilters: SearchFilter[]
  persistedFilters: string[]
  preAppliedFilters: {
    key: string
    group: string
  }[]
  selectedFilters: ApplicableFilter[]
  hasNbaFilters?: boolean
  hasRudeFilterEnabled?: boolean
  filtersQuery: ({
    filters,
    integerFilter,
  }: {
    filters: SearchFilter[]
    integerFilter?: IntegerFilterInput
  }) => Promise<LoadFiltersResponse>
  services: SearchService
  allFilters: FilterItem[]
  quickFilterKeys: string[]
  sponsoredProducts?: SponsoredProductInput[]
  offset: number
  galleryPageSize: number
  inlineTickerMessages?: string[]
  filteringEvent?: FilteringEvent
  recipientsWithReminders: RecipientWithReminders[]
}

export const buildInitialState = ({
  pageTitle,
  pageType,
  router,
  department,
  initialResponse,
  redirectedFrom,
  redirectionSource,
  searchTerm,
  correctedSearchTerm,
  selectedSuggestion,
  isTrendingSuggestion,
  promotionId,
  collection,
  preAppliedFilters,
  selectedFilters,
  hasNbaFilters,
  hasRudeFilterEnabled,
  filtersQuery,
  services,
  allFilters,
  quickFilterKeys,
  sponsoredProducts,
  offset,
  galleryPageSize,
  inlineTickerMessages,
  filteringEvent,
  recipientsWithReminders,
}: BuildInitialStateArgs): Partial<SearchStoreState> => {
  return {
    pageContext: {
      title: pageTitle,
      type: pageType,
      inlineTickerMessages: inlineTickerMessages || [],
      router,
      collection,
    },
    searchContext: {
      departments: department,
      departmentCounts: initialResponse.departments,
      redirectedFrom,
      redirectionSource,
      searchTerm,
      correctedSearchTerm,
      selectedSuggestion,
      isTrendingSuggestion,
      promotionId,
    },
    recipientsWithReminders,
    filters: allFilters,
    quickFilterKeys,
    integerFilters: extractIntegerFilters(allFilters),
    preAppliedFilters,
    selectedFilters,
    queryFilters: async ({
      filters,
      integerFilter,
    }: {
      filters: SearchFilter[]
      integerFilter?: IntegerFilterInput
    }) => {
      const result = await filtersQuery({ filters, integerFilter })
      return {
        filters: removePreAppliedFilters(result.filters, preAppliedFilters),
        noResultsFilters: result.noResultsFilters,
        quickFilterKeys: result.quickFilterKeys ?? [],
      }
    },
    sortValue: SortOrder.POPULARITY,
    initialSortValue: SortOrder.POPULARITY,
    results: initialResponse,
    hasNbaFilters,
    hasRudeProducts: hasRudeFilterEnabled,
    initialOffset: offset,
    galleryPageSize: galleryPageSize,
    querySearch: async ({
      newPage,
      filters,
      integerFilter = {},
      sortOrder,
    }: {
      newPage: number
      filters: SearchFilter[]
      integerFilter?: IntegerFilterInput
      sortOrder: SortOrder | undefined
    }) => {
      const searchResponse = await services.search({
        searchTerm,
        departments: department,
        sortOrder,
        filters,
        integerFilter,
        page: newPage,
        promotionId,
        sponsoredProducts: sponsoredProducts || /* istanbul ignore next */ [],
      })
      return searchResponse
    },
    topPage: initialResponse.page,
    bottomPage: initialResponse.page,
    lastLoadedPage: initialResponse.page,
    filtersMenuOpen: false,
    loading: false,
    filteringEvent,
  }
}
